/**
 * M3 WORLD B3: signal-source renderer. Pooled beacons (a distinct colour from the
 * amber combat / cyan body palette) placed by floating-origin delta like
 * projectiles.ts / npcs.ts. Each live signal gets a glowing ring + core so it
 * reads on the radar and in the canopy as a droppable point of interest. The
 * dropped-into derelict HULL is a separate GLB (desaturated) with a box fallback
 * so a missing asset never blocks the scene.
 *
 * Distance-based LOD (perf): derelict GLB shown when near, cheap proxy box beyond
 * FULL_DERELICT_KM, both hidden beyond PROXY_DERELICT_KM.  Thresholds live in
 * render/lod.ts.  The missing-GLB fallback box is preserved as-is (it acts as
 * fallback AND near-view mesh when the GLB is absent).
 */
import * as THREE from 'three';
import { assetDir } from './assetDir.js';
import { createGltfLoader } from './gltf.js';
import { signalMarkerPos, type Vec3, type SignalSource, type SignalKind } from '@blackmarble/sim-core';
import { normaliseToRadius, enableShadows } from './bodyFactory.js';
import { applyLod, createGlintSprite, FULL_DERELICT_KM, PROXY_DERELICT_KM } from './lod.js';

const POOL = 8; // matches the seeded signal cap with headroom
const RING_RADIUS_KM = 120;
const DERELICT_DISPLAY_KM = 20;
const BASE_BEACON_OPACITY = 0.7;
const FADE_HIDE_KM = 300;
const FADE_FULL_KM = 1_500;
const MIN_BEACON_SCALE = 0.35;

/**
 * Within this distance (km) of a signal whose kind has a dedicated 3-D asset
 * (derelict GLB, cenotaph hero, or anomaly placeholder), the procedural
 * beacon ring+core is hidden so the real asset dominates. Far away the beacon
 * still acts as a navigational marker. Tuned so the GLB's LOD full-detail zone
 * (FULL_DERELICT_KM ≈ 347 km) is well inside this suppression bubble.
 */
const SUPPRESS_NEAR_KM = 500;

/** Sombre signal palette by kind (no triumphant colours). */
const COLOR_FOR: Record<SignalKind, number> = {
  derelict: 0x9b8f7a, // bone / dust
  distress: 0xc0504d, // muted alarm red
  wreckfield: 0x7a8a99, // cold steel
  ambush: 0xb03030, // deeper threat red
  mining: 0x6f9a7a, // dull green
  anomaly: 0x8a6fb0, // violet (the Silence's shadow)
  memorial: 0xaeb6c0, // pale slate (the Cenotaph)
};

const FADE_KINDS = new Set<SignalKind>(['ambush', 'distress', 'wreckfield', 'mining']);

export class SignalRenderer {
  private readonly rings: THREE.Mesh[] = [];
  private readonly cores: THREE.Mesh[] = [];
  private readonly derelict: THREE.Group;
  /** The loaded GLB scene inside the derelict group, or null when absent. */
  private derelictGlb: THREE.Object3D | null = null;
  /** Cheap proxy box — always in the derelict group, sized to display radius. */
  private readonly derelictProxy: THREE.Mesh;
  /** Far-LOD sun glint so the dead hull never vanish-pops past proxy range. */
  private readonly derelictGlint: THREE.Sprite;
  private derelictLoaded = false;

  constructor(private readonly parent: THREE.Group) {
    const ringGeo = new THREE.TorusGeometry(RING_RADIUS_KM, RING_RADIUS_KM * 0.06, 6, 32);
    const coreGeo = new THREE.SphereGeometry(RING_RADIUS_KM * 0.18, 8, 8);
    for (let i = 0; i < POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        toneMapped: false,
        transparent: true,
        opacity: BASE_BEACON_OPACITY,
      });
      const ring = new THREE.Mesh(ringGeo, mat);
      const core = new THREE.Mesh(coreGeo, mat.clone());
      ring.visible = false;
      core.visible = false;
      this.rings.push(ring);
      this.cores.push(core);
      parent.add(ring);
      parent.add(core);
    }
    this.derelict = new THREE.Group();
    this.derelict.visible = false;
    this.derelictProxy = makeDerelictProxy();
    this.derelict.add(this.derelictProxy);
    this.derelictGlint = createGlintSprite();
    this.derelict.add(this.derelictGlint);
    parent.add(this.derelict);
  }

  /** Lazy-load the derelict hull GLB with a desaturated box fallback. */
  async loadDerelict(url = `${assetDir('ships')}derelict.glb`): Promise<void> {
    if (this.derelictLoaded) return;
    this.derelictLoaded = true;
    try {
      const gltf = await createGltfLoader().loadAsync(url);
      normaliseToRadius(gltf.scene, DERELICT_DISPLAY_KM);
      gltf.scene.traverse((o) => {
        const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
        if (m && 'color' in m) {
          m.color.multiplyScalar(0.5); // desaturate / darken — a dead hull
          m.metalness = 0.2;
          m.roughness = 0.9;
        }
      });
      enableShadows(gltf.scene); // WAVE 2a: the dead hull self-shadows (harder, more austere)
      this.derelict.add(gltf.scene);
      this.derelictGlb = gltf.scene;
      // Proxy starts hidden; applyLod() manages it each frame.
      this.derelictProxy.visible = false;
    } catch {
      // No GLB — proxy box remains as the only mesh (fallback).
      // Do NOT add a second box; the proxy already serves as the fallback.
    }
  }

  /** Place the live signal beacons (floating-origin); hide the rest. */
  update(
    signals: readonly SignalSource[],
    shipWorldPos: Vec3,
    simTime: number,
    activeSignalId: string | null,
  ): void {
    for (let i = 0; i < POOL; i++) {
      const ring = this.rings[i] as THREE.Mesh;
      const core = this.cores[i] as THREE.Mesh;
      const sig = signals[i];
      if (!sig) {
        ring.visible = false;
        core.visible = false;
        continue;
      }
      const marker = signalMarkerPos(sig);
      const dx = marker.x - shipWorldPos.x;
      const dy = marker.y - shipWorldPos.y;
      const dz = marker.z - shipWorldPos.z;
      ring.position.set(dx, dy, dz);
      core.position.set(dx, dy, dz);
      const distKm = Math.hypot(dx, dy, dz);
      const fade =
        activeSignalId === sig.id
          ? 0
          : beaconFade(sig.kind, distKm, dx, dy, dz);
      if (fade <= 0.001) {
        ring.visible = false;
        core.visible = false;
        continue;
      }
      const col = COLOR_FOR[sig.kind];
      const opacity = BASE_BEACON_OPACITY * fade;
      const scale = lerp(MIN_BEACON_SCALE, 1, fade);
      applyBeaconVisual(ring, col, opacity, scale);
      applyBeaconVisual(core, col, opacity, scale);
      ring.rotation.z = simTime * 0.2; // slow, sombre rotation
      ring.visible = true;
      core.visible = true;
    }
  }

  /** Show/place the dropped-in derelict hull at a signal centre (or hide it). */
  updateDerelict(centre: Vec3 | null, shipWorldPos: Vec3): void {
    if (!centre) {
      this.derelict.visible = false;
      return;
    }
    this.derelict.visible = true;
    this.derelict.position.set(
      centre.x - shipWorldPos.x,
      centre.y - shipWorldPos.y,
      centre.z - shipWorldPos.z,
    );
    if (this.derelictGlb !== null) {
      // GLB present: apply LOD — full vs proxy vs hidden + far glint.
      applyLod(
        this.derelict.position, this.derelictGlb, this.derelictProxy,
        FULL_DERELICT_KM, PROXY_DERELICT_KM, this.derelictGlint,
      );
    } else {
      // GLB absent: proxy box is the only mesh — always visible (fallback); no glint.
      this.derelictGlint.visible = false;
    }
  }
}

/** Kinds whose dedicated 3-D asset should dominate within SUPPRESS_NEAR_KM. */
const ASSET_KINDS = new Set<SignalKind>(['derelict', 'memorial', 'anomaly']);

/**
 * Returns true when the beacon should be hidden because the signal has a
 * dedicated 3-D asset and the ship is close enough for it to dominate.
 * dx/dy/dz are the floating-origin deltas already computed in update().
 */
function shouldSuppressBeacon(kind: SignalKind, dx: number, dy: number, dz: number): boolean {
  if (!ASSET_KINDS.has(kind)) return false;
  const distSq = dx * dx + dy * dy + dz * dz;
  return distSq < SUPPRESS_NEAR_KM * SUPPRESS_NEAR_KM;
}

function beaconFade(kind: SignalKind, distKm: number, dx: number, dy: number, dz: number): number {
  if (shouldSuppressBeacon(kind, dx, dy, dz)) return 0;
  if (!FADE_KINDS.has(kind)) return 1;
  return smoothstep(FADE_HIDE_KM, FADE_FULL_KM, distKm);
}

function applyBeaconVisual(mesh: THREE.Mesh, color: number, opacity: number, scale: number): void {
  const mat = mesh.material as THREE.MeshBasicMaterial;
  mat.color.setHex(color);
  mat.opacity = opacity;
  mesh.scale.setScalar(scale);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Build a cheap proxy box sized to the derelict display dimensions. */
function makeDerelictProxy(): THREE.Mesh {
  const geo = new THREE.BoxGeometry(DERELICT_DISPLAY_KM, DERELICT_DISPLAY_KM * 0.4, DERELICT_DISPLAY_KM * 2);
  const mat = new THREE.MeshStandardMaterial({ color: 0x55504a, roughness: 0.95, metalness: 0.15 });
  const box = new THREE.Mesh(geo, mat);
  box.castShadow = true; // WAVE 2a
  box.receiveShadow = true;
  return box;
}
